import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Alert,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Grid,
  Tabs,
  Tab,
  Badge,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Source as SourceIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Lightbulb as LightbulbIcon,
  GpsFixed as TargetIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Source } from "../types";
import ScrollableBox from "../../../components/common/ScrollableBox";
import { MarkdownContentBox } from "../../../components/common/MarkdownContentBox";
import { ProgressCard } from "./ProgressCard";
import { useAlert } from "../../../context/AlertContext";
import { useResearch } from "../../../context/ResearchContext";
import { getStatusLabel, getStatusColor } from "../api";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`research-tabpanel-${index}`}
      aria-labelledby={`research-tab-${index}`}
      sx={{
        height: "100%",
        overflowY: "auto",
        display: value === index ? "block" : "none",
        flexGrow: 1,
      }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `research-tab-${index}`,
    "aria-controls": `research-tabpanel-${index}`,
  };
}

interface ResearchTabGroupProps {
  onEditQuery: (editQuery: string) => void;
}

export const ResearchTabGroup: React.FC<ResearchTabGroupProps> = ({ onEditQuery }) => {
  const [editQuery, setEditQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const tabsSwitchRef = useRef<boolean>(false);

  // コンテキストから状態を取得
  const { research, progressState, activeTab, setActiveTab, editingPlan, setEditingPlan, forceUpdate } = useResearch();

  // researchがnullの場合用のdefault値
  const safeResearch = research || {
    status: "",
    sources: [],
    plan_text: "",
    uuid: "",
    query: "",
    research_mode: "",
    total_sources: 0,
    unique_domains: 0,
    created_at: "",
    completed_at: "",
    progress_percentage: 0,
    edit_history: [],
  };

  const isPending = safeResearch.status === "plan_pending";
  const isCompleted = safeResearch.status === "completed";
  const isPlanning = safeResearch.status === "planning";
  const isAnalyzing = safeResearch.status === "analyzing";
  const isResearching = safeResearch.status === "researching";
  const isGenerating = safeResearch.status === "generating";
  const isInProgress = isPlanning || isAnalyzing || isResearching || isGenerating;

  // タブの設定をuseMemoで最適化
  const tabs = useMemo(
    () => [
      {
        label: t("deepResearch.tabs.researchInfo"),
        icon: <InfoIcon />,
        content: null, // 後で設定
      },
      ...(safeResearch.sources && safeResearch.sources.length > 0
        ? [
            {
              label: t("deepResearch.tabs.sources"),
              icon: <SourceIcon />,
              badge: safeResearch.sources.length,
              content: null, // 後で設定
            },
          ]
        : []),
      ...(safeResearch.plan_text || isPending || isPlanning || isAnalyzing
        ? [
            {
              label: isPending
                ? t("deepResearch.tabs.planReview")
                : isCompleted
                  ? t("deepResearch.tabs.executedPlan")
                  : isPlanning
                    ? t("deepResearch.tabs.planGenerating")
                    : isAnalyzing
                      ? t("deepResearch.tabs.initialAnalysis")
                      : t("deepResearch.tabs.researchPlan"),
              icon: <AssignmentIcon />,
              badge:
                (isPlanning || isAnalyzing) && !safeResearch.plan_text ? t("deepResearch.tabs.processing") : undefined,
              content: null, // 後で設定
            },
          ]
        : []),
    ],
    [safeResearch.sources, safeResearch.plan_text, isPending, isCompleted, isPlanning, isAnalyzing, t]
  );

  // タブの長さが変化した時の処理（plan_pendingの時の自動切り替えのみ）
  useEffect(() => {
    if (!research) return;

    // タブが自動切り替えされた場合は何もしない
    if (tabsSwitchRef.current) {
      tabsSwitchRef.current = false;
      return;
    }

    // 計画完成時のタブ切り替え
    if (research.status === "plan_pending" && tabs.length > 0) {
      const planTabIndex = tabs.length - 1;
      setActiveTab(planTabIndex);
    }
  }, [research, tabs.length, setActiveTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditSubmit = async () => {
    if (!editQuery.trim()) return;

    setSubmitting(true);
    try {
      setTimeout(() => setAlert("info", t("deepResearch.success.planEditStarted")), 100);
      await onEditQuery(editQuery.trim());
      setEditQuery("");
      setEditingPlan(false);
    } catch {
      setTimeout(() => setAlert("error", t("deepResearch.errors.planEditFailed")), 100);
    } finally {
      setSubmitting(false);
    }
  };

  // researchがない場合の表示
  if (!research) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography>{t("deepResearch.info.noData")}</Typography>
      </Box>
    );
  }

  // 調査情報セクションの内容
  const renderInfoContent = () => (
    <ScrollableBox sx={{ maxHeight: "calc(100vh - 200px)", p: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* 進捗カード（進捗がある場合のみ表示、条件を緩和） */}
        {(progressState.currentProgress > 0 || research.progress_percentage > 0 || isResearching || isGenerating) && (
          <ProgressCard
            currentProgress={
              progressState.currentProgress > 0 ? progressState.currentProgress : research.progress_percentage || 0
            }
            currentMessage={progressState.currentMessage || t("deepResearch.info.researching")}
            status={research.status}
            researchMode={research.research_mode}
            searchCount={progressState.searchCount > 0 ? progressState.searchCount : undefined}
            maxSearches={progressState.maxSearches > 0 ? progressState.maxSearches : undefined}
            currentQuery={progressState.currentQuery}
          />
        )}

        {/* ユーザークエリ */}
        <Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
            {t("deepResearch.info.query")}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: "300px", overflowY: "auto" }}>
            <Typography variant="body1">{research.query}</Typography>
          </Paper>
        </Box>

        {/* ステータスと進捗（ストリーミング進捗を優先表示） */}
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {t("deepResearch.info.status")}
            </Typography>
            {(() => {
              // 理想的な設計：ストリーミングステータスを直接使用
              const currentStatus =
                research.status ||
                progressState.currentStatus || // ストリーミングから直接取得
                "";

              if (currentStatus && getStatusLabel(currentStatus)) {
                return (
                  <Chip
                    label={t(getStatusLabel(currentStatus))}
                    color={
                      getStatusColor(currentStatus) as
                        | "default"
                        | "primary"
                        | "secondary"
                        | "error"
                        | "info"
                        | "success"
                        | "warning"
                    }
                    size="small"
                  />
                );
              } else if (currentStatus) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    {currentStatus}
                  </Typography>
                );
              } else if (isResearching || isGenerating) {
                return (
                  <Chip
                    label={t(isGenerating ? "deepResearch.status.generating" : "deepResearch.status.researching")}
                    color="info"
                    size="small"
                  />
                );
              }
              return null;
            })()}
          </Box>

          {research.research_mode && (
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t("deepResearch.info.researchMode")}
              </Typography>
              <Typography variant="body1">
                {research.research_mode === "fast"
                  ? t("deepResearch.researchMode.fast")
                  : t("deepResearch.researchMode.detailed")}
              </Typography>
            </Box>
          )}

          {/* リアルタイム進捗表示 */}
          {(progressState.currentProgress > 0 || research.progress_percentage > 0) && (
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t("deepResearch.info.progress")}
              </Typography>
              <Typography variant="body1">
                {progressState.currentProgress > 0 ? progressState.currentProgress : research.progress_percentage}%
              </Typography>
            </Box>
          )}

          {/* リアルタイム検索数表示 */}
          {(progressState.searchCount > 0 || progressState.maxSearches > 0) && (
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t("deepResearch.info.searchProgress")}
              </Typography>
              <Typography variant="body1">
                {t("deepResearch.progress.searchProgress", {
                  current: progressState.searchCount,
                  total: progressState.maxSearches,
                })}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {t("deepResearch.info.sourcesCount")}
            </Typography>
            <Typography variant="body1">
              {t("deepResearch.list.sourcesCount", { count: research.total_sources })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {t("deepResearch.info.uniqueDomainsCount")}
            </Typography>
            <Typography variant="body1">
              {t("deepResearch.list.sourcesCount", { count: research.unique_domains })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {t("deepResearch.info.createdAt")}
            </Typography>
            <Typography variant="body1">{new Date(research.created_at).toLocaleString()}</Typography>
          </Box>

          {research.completed_at && (
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t("deepResearch.info.completedAt")}
              </Typography>
              <Typography variant="body1">{new Date(research.completed_at).toLocaleString()}</Typography>
            </Box>
          )}
        </Box>

        {/* リアルタイム状態メッセージ（進行中の場合のみ表示） */}
        {isInProgress &&
          progressState.currentMessage &&
          progressState.currentMessage !== t("deepResearch.info.researching") && (
            <Box>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                {t("deepResearch.info.currentProcess")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="primary">
                  {progressState.currentMessage}
                </Typography>
              </Paper>
            </Box>
          )}
      </Box>
    </ScrollableBox>
  );

  // 情報源セクションの内容
  const renderSourcesContent = () => (
    <ScrollableBox sx={{ maxHeight: "calc(100vh - 193px)" }}>
      <List sx={{ width: "100%", px: 2, my: 2 }}>
        {research.sources?.map((source: Source, index: number) => (
          <React.Fragment key={index}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography
                    component="a"
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none", color: "primary.main" }}
                  >
                    {source.title}
                  </Typography>
                }
                secondary={
                  <Box>
                    {source.snippet && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {source.snippet}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        {new URL(source.url).hostname}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(source.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
            {index < (research.sources?.length || 0) - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    </ScrollableBox>
  );

  // 調査計画セクションの内容
  const renderPlanContent = () => (
    <Box sx={{ height: "calc(100vh - 193px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* 計画生成中の詳細表示 */}
      {(isPlanning || isAnalyzing) && !research.plan_text && (
        <ScrollableBox sx={{ maxHeight: "100%", flexGrow: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {isAnalyzing ? <SearchIcon /> : <PsychologyIcon />}
                <Typography variant="subtitle1">
                  {isAnalyzing ? t("deepResearch.process.initialAnalysis") : t("deepResearch.process.planGeneration")}
                </Typography>
              </Box>
              <Typography variant="body2">
                {isAnalyzing
                  ? t("deepResearch.process.initialAnalysisDescription")
                  : t("deepResearch.process.planGenerationDescription")}
              </Typography>
            </Alert>

            {/* 進行状況の視覚的表示 */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body1" fontWeight="medium">
                  {isAnalyzing ? t("deepResearch.process.analyzing") : t("deepResearch.process.generating")}
                </Typography>
              </Box>

              <LinearProgress sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {isAnalyzing
                  ? t("deepResearch.process.steps.queryAnalysis")
                  : t("deepResearch.process.steps.planGeneration")}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {t("deepResearch.process.timeEstimate")}
              </Typography>
            </Paper>
          </Box>
        </ScrollableBox>
      )}

      {/* Gridレイアウト: 左に計画、右に編集 */}
      {research.plan_text && (
        <Grid container spacing={0} sx={{ flexGrow: 1, height: "100%" }}>
          {/* 左側: 調査計画 */}
          <Grid size={{ xs: 12, md: isPending ? 6 : 12 }} sx={{ height: "100%" }}>
            <ScrollableBox sx={{ maxHeight: "100%", height: "100%", px: 2, "& code": { whiteSpace: "pre-wrap" } }}>
              <Box sx={{ py: 2 }}>
                {isPending && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t("deepResearch.planEdit.approvalMessage")}
                  </Alert>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    my: 2,
                    ...(editingPlan && {
                      borderColor: "primary.main",
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <TargetIcon color="primary" />
                    <Typography variant="subtitle1">
                      {editingPlan
                        ? t("deepResearch.planEdit.currentPlanEditing")
                        : t("deepResearch.planEdit.planTitle")}
                      :
                    </Typography>
                  </Box>
                  <MarkdownContentBox>{research.plan_text}</MarkdownContentBox>
                </Paper>

                {/* ヒントメッセージ - 左側の下 */}
                {isPending && (
                  <Box sx={{ p: 2, borderRadius: 1, border: 1, borderColor: "divider", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <LightbulbIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t("deepResearch.planEdit.startMessage")}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </ScrollableBox>
          </Grid>

          {/* 右側: 編集エリア（計画承認待ちの時は常に表示） */}
          {isPending && (
            <Grid size={{ xs: 12, md: 6 }} sx={{ height: "100%" }}>
              <ScrollableBox sx={{ maxHeight: "100%", height: "100%" }}>
                <Box sx={{ py: 2, px: 2 }}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <EditIcon color="primary" />
                      <Typography variant="subtitle1">{t("deepResearch.planEdit.title")}:</Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={8}
                      value={editQuery}
                      onChange={(e) => setEditQuery(e.target.value)}
                      placeholder={t("deepResearch.planEdit.placeholder")}
                      sx={{ mb: 2 }}
                      helperText={t("deepResearch.planEdit.helperText")}
                    />

                    {/* 編集ボタン */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        disableElevation
                        variant="contained"
                        color="secondary"
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        onClick={handleEditSubmit}
                        disabled={!editQuery.trim() || submitting}
                      >
                        {submitting ? t("deepResearch.planEdit.sending") : t("deepResearch.planEdit.sendInstruction")}
                      </Button>
                    </Box>
                  </Paper>

                  {/* 編集履歴の表示 */}
                  {research.edit_history && research.edit_history.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <HistoryIcon color="action" />
                        <Typography variant="subtitle1">{t("deepResearch.planEdit.editHistory")}:</Typography>
                      </Box>
                      {research.edit_history.map((edit, index) => (
                        <Typography key={index} variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          • {edit.query} <em>({new Date(edit.timestamp).toLocaleString()})</em>
                        </Typography>
                      ))}
                    </Paper>
                  )}
                </Box>
              </ScrollableBox>
            </Grid>
          )}
        </Grid>
      )}

      {/* エラー状態の表示 */}
      {research.status === "error" && !research.plan_text && (
        <ScrollableBox sx={{ maxHeight: "100%", flexGrow: 1 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {t("deepResearch.errors.planCreationFailed")}
          </Alert>
        </ScrollableBox>
      )}
    </Box>
  );

  // タブにコンテンツを設定
  const tabsWithContent = tabs.map((tab, index) => ({
    ...tab,
    content:
      index === 0
        ? renderInfoContent()
        : tab.label === t("deepResearch.tabs.sources")
          ? renderSourcesContent()
          : renderPlanContent(),
  }));

  return (
    <Box
      key={forceUpdate}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
      className="research-tab-group"
    >
      {/* タブヘッダー */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="research tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabsWithContent.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={
                tab.badge ? (
                  <Badge
                    badgeContent={tab.badge}
                    color={tab.badge === t("deepResearch.tabs.processing") ? "secondary" : "primary"}
                    sx={{
                      "& .MuiBadge-badge": {
                        position: "relative",
                        transform: "none",
                        ml: 1,
                      },
                    }}
                  >
                    {tab.label}
                  </Badge>
                ) : (
                  tab.label
                )
              }
              {...a11yProps(index)}
              sx={{
                minHeight: 64,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* タブコンテンツ */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {tabsWithContent.map((tab, index) => (
          <TabPanel key={index} value={activeTab} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};
